import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import type { CharactersData } from '@packages/global';
import { IProjectAsset } from '@packages/global';
import CharacterCard from '../cards/CharacterCard';
import NarratorCard from '../cards/NarratorCard';
import BgmCard from '../cards/BgmCard';

/**
 * CharactersStep 组件属性
 */
export interface CharactersStepProps {
  /** 步骤数据 */
  data: CharactersData | null;
  /** 脚本 ID */
  scriptId?: string;
  /** 项目 ID */
  projectId?: string;
  /** 集 ID */
  episodeId?: string;
  /** 旁白音色 URL */
  narratorVoiceUrl?: string;
  /** BGM URL */
  bgmUrl?: string;
  /** 艺术风格 */
  artStyle?: string;
  /** 是否正在加载 */
  isLoading?: boolean;
  /** 图片正在生成中（步骤 IN_PROGRESS），禁用卡片操作 */
  imageGenerating?: boolean;
  /** 来自 ProjectAsset 的主体列表，用于实时反映生成状态和图片 */
  episodeAssets?: IProjectAsset[];
  /** 重新生成回调 */
  onRegenerate?: () => void;
  /** 数据更新回调 */
  onUpdate?: () => void;
}

/**
 * 空状态组件
 */
function EmptyState({ onRegenerate }: { onRegenerate?: () => void }) {
  const { t } = useTranslation(['script', 'common']);
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 rounded-full bg-bg-elevated flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
        </svg>
      </div>
      <h3 className="text-base font-medium text-gray-200 mb-2">{t('script:characters.empty')}</h3>
      <p className="text-sm text-gray-400 mb-6 max-w-xs">
        {t('script:characters.emptyDesc')}
      </p>
      {onRegenerate && (
        <button
          onClick={onRegenerate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-medium transition-colors cursor-pointer"
        >
          <ArrowPathIcon className="w-4 h-4" />
          {t('script:characters.generate')}
        </button>
      )}
    </div>
  );
}

/**
 * CharactersStep 组件
 * 
 * 复用原有的 CharacterCard 组件，支持：
 * - 图片上传/AI生成
 * - 音色选择
 * - 删除角色
 * 
 * Requirements: 6.5
 */
export default function CharactersStep({
  data,
  scriptId,
  projectId,
  episodeId,
  narratorVoiceUrl,
  bgmUrl,
  artStyle,
  isLoading = false,
  imageGenerating = false,
  episodeAssets = [],
  onRegenerate,
  onUpdate,
}: CharactersStepProps) {
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { t } = useTranslation(['script', 'common']);

  // 播放音频
  const handlePlayAudio = (url: string, id: string) => {
    if (playingAudio === id) {
      audioRef.current?.pause();
      setPlayingAudio(null);
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(url);
      audio.onended = () => setPlayingAudio(null);
      audio.play();
      audioRef.current = audio;
      setPlayingAudio(id);
    }
  };

  // 加载状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="w-8 h-8 text-primary animate-spin" />
          <span className="text-sm text-gray-400">{t('script:characters.generating')}</span>
        </div>
      </div>
    );
  }

  // 空状态
  if (!data || data.characters.length === 0) {
    return <EmptyState onRegenerate={onRegenerate} />;
  }

  return (
    <div className="space-y-5">
      {/* 角色网格 - 旁白卡片 + 角色卡片 */}
      <div className="flex flex-wrap gap-4">
        {/* 旁白角色卡片 */}
        {narratorVoiceUrl && scriptId && (
          <div className="w-[200px] flex-shrink-0">
            <NarratorCard
              narratorVoiceUrl={narratorVoiceUrl}
              scriptId={scriptId}
              playingAudio={playingAudio}
              onPlayAudio={handlePlayAudio}
              onUpdate={onUpdate}
            />
          </div>
        )}

        {/* BGM 卡片 */}
        {bgmUrl && scriptId && (
          <div className="w-[200px] flex-shrink-0">
            <BgmCard
              bgmUrl={bgmUrl}
              scriptId={scriptId}
              playingAudio={playingAudio}
              onPlayAudio={handlePlayAudio}
              onUpdate={onUpdate}
            />
          </div>
        )}

        {data.characters.map((character, index) => {
          // 按名称从 episodeAssets 中查找对应的 ProjectAsset，获取实时状态
          const matchedAsset = episodeAssets.find(a => a.name === character.name);
          return (
            <div key={index} className="w-[200px] flex-shrink-0">
              <CharacterCard
                character={character}
                playingAudio={playingAudio}
                onPlayAudio={handlePlayAudio}
                scriptId={scriptId || ''}
                projectId={projectId}
                episodeId={episodeId}
                artStyle={artStyle}
                imageGenerating={imageGenerating}
                assetGenerationStatus={matchedAsset?.generationStatus}
                assetImageUrl={matchedAsset?.conceptImageUrl || matchedAsset?.frontViewUrl}
                matchedAsset={matchedAsset}
                onUpdate={onUpdate}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
